import { governanceChecks, type GovernanceRecord } from '../fixtures/governance-data';
import {
  reviewRuleContext,
  reviewRuleRefusal,
  reviewRuleScenarios,
  reviewStatusLabels,
  reviewVersionFixtures,
  type ReviewCaseState,
  type ReviewRuleScenario,
} from '../fixtures/review-demo-fixtures';
import './review-queue-prototype.css';

export function ReviewVersionComparison({ record }: { record: GovernanceRecord }) {
  const versions = reviewVersionFixtures[record.id];
  if (!versions) return <p className="review-demo-warning">来源 / 版本未知，无法进行比较。</p>;
  const fields = [
    { key: 'purpose', label: '用途声明' },
    { key: 'materials', label: '资料目录' },
    { key: 'restrictions', label: '限制与风险提示' },
    { key: 'source', label: '来源声明' },
  ] as const;
  return (
    <section className="governance-versions review-demo-versions">
      <h3>版本变更对比（字段级）</h3>
      <p className="review-demo-note">
        v1为只读已发布固定演示样例；v2为独立08提交样例。未知与未核验信息保留原义。
      </p>
      <div className="governance-version-grid">
        <article>
          <header>
            上一版 v1<small>只读固定样例 · PUBLISHED · DEMO</small>
          </header>
          <ul>
            {fields.map(({ key, label }) => (
              <li key={key}>
                <strong>{label}：</strong>
                {versions.v1[key]}
              </li>
            ))}
          </ul>
        </article>
        <article>
          <header>
            本次提交 v2 <span>主要变更</span>
            <small>本次示例提交：{record.submitted.slice(0, 10)}</small>
          </header>
          <ul>
            {fields.map(({ key, label }) => (
              <li className="review-demo-changed" key={key}>
                <strong>{label}（已变更）：</strong>
                {versions.v2[key]}
              </li>
            ))}
          </ul>
        </article>
      </div>
    </section>
  );
}
export function ReviewQueuePrototype({
  record,
  item,
  onOpinion,
  onScenario,
  onAction,
  onReset,
  onReadback,
}: {
  record: GovernanceRecord;
  item: ReviewCaseState;
  onOpinion: (value: string) => void;
  onScenario: (value: ReviewRuleScenario) => void;
  onAction: (action: 'return' | 'agree') => void;
  onReset: () => void;
  onReadback: () => void;
}) {
  const context = reviewRuleContext(record, item);
  const refusal = reviewRuleRefusal(context);
  return (
    <aside className="portal-card governance-review review-demo-workflow">
      <div className="portal-card-head">
        <h2>审核检查项</h2>
        <span>共 8 项</span>
      </div>
      <div className="governance-checks">
        {governanceChecks.map(([title, description, state]) => (
          <div key={title}>
            <span
              className={`governance-check-icon ${state === '已列示' ? 'governance-check-listed' : ''}`}
            >
              {state === '已列示' ? '✓' : '!'}
            </span>
            <div>
              <strong>{title}</strong>
              <small>{description}</small>
            </div>
            <span
              className={`governance-check-state ${state === '已列示' ? 'governance-check-listed' : ''}`}
            >
              {state}
            </span>
          </div>
        ))}
      </div>
      <div className="review-demo-local-state">
        <strong>{reviewStatusLabels[item.status]} · DEMO</strong>
        <small>
          {item.status} · {item.demoReviewId}
        </small>
        <p>仅08本次会话 · 刷新重新初始化静态样例</p>
      </div>
      <h3>审核意见</h3>
      <label className="governance-opinion">
        <span className="governance-sr-only">模拟审核意见</span>
        <textarea
          aria-describedby="review-opinion-note"
          value={item.opinion}
          onChange={(e) => onOpinion(e.target.value)}
          placeholder="请输入模拟审核意见 / 退回原因…"
        />
        <small>{item.opinion.length}/500</small>
      </label>
      <p id="review-opinion-note" className="review-demo-note">
        原因必填，限500字纯文本。不会保存正式审核决定或向提交人发送。
      </p>
      <div className="governance-opinion-actions">
        <button
          type="button"
          className="portal-button governance-return"
          onClick={() => onAction('return')}
        >
          模拟退回
        </button>
        <button type="button" className="portal-button" onClick={() => onAction('agree')}>
          模拟形成审核意见
        </button>
      </div>
      {item.message && (
        <p className="governance-receipt review-demo-message" role="status" aria-live="polite">
          {item.message}
        </p>
      )}
      <section className="review-demo-publication">
        <h3>发布协调 · 流程示意</h3>
        <ol>
          <li className={item.status === 'REVIEW_OPINION_DEMO' ? 'is-current' : ''}>
            本地意见：
            {item.status === 'REVIEW_OPINION_DEMO'
              ? '已形成赞同审核意见（演示）'
              : '尚未形成赞同意见'}
          </li>
          <li>
            等待{record.kind === 'PRODUCT_MODEL' ? '06 Product Model' : '05 LocalAsset'} Owner发布
          </li>
          <li>发布结果读回待核对 · 当前不可用</li>
        </ol>
        <button className="review-demo-link-button" onClick={onReadback}>
          查看发布结果读回状态
        </button>
        <p className="review-demo-note">无正式PublishedRef，无Owner确认，不会标记新PUBLISHED。</p>
      </section>
      <section className="review-demo-rules">
        <h3>规则拒绝演示</h3>
        <label>
          选择条件样例
          <select
            aria-label="审核规则场景"
            value={item.scenario}
            onChange={(e) => onScenario(e.target.value as ReviewRuleScenario)}
          >
            {Object.entries(reviewRuleScenarios).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <dl>
          <dt>提交账户</dt>
          <dd>{context.makerAccount}</dd>
          <dt>审核账户</dt>
          <dd>{context.reviewerAccount}</dd>
          <dt>提交自然人</dt>
          <dd>{context.makerNaturalPerson}</dd>
          <dt>审核自然人</dt>
          <dd>{context.reviewerNaturalPerson}</dd>
          <dt>资料Scope</dt>
          <dd>{context.subjectScope}</dd>
          <dt>审核Scope</dt>
          <dd>{context.reviewerScope}</dd>
          <dt>Permission</dt>
          <dd>{context.permission ? '具备（合成样例）' : '缺少（合成样例）'}</dd>
          <dt>打开 / 当前版本</dt>
          <dd>
            {context.viewedVersion} / {context.submittedVersion}
          </dd>
        </dl>
        {refusal && <p className="review-demo-warning">{refusal}</p>}
        <p className="review-demo-note">
          全部身份与范围为合成教学标识。前端规则演示不等于服务端安全、可信身份、并发或幂等已实现。
        </p>
        <button type="button" className="review-demo-link-button" onClick={onReset}>
          恢复本条静态样例（清除本条会话意见）
        </button>
      </section>
      <section className="review-demo-history">
        <h3>当前会话操作回放</h3>
        {item.history.length === 0 ? (
          <p className="review-demo-note">暂无本地操作；无正式审计ID。</p>
        ) : (
          <ul>
            {item.history.map((value, index) => (
              <li key={index}>{value}</li>
            ))}
          </ul>
        )}
      </section>
      <div className="governance-important">
        <strong>ⓘ 重要说明 · 非正式审核</strong>
        <p>
          意见演示不等于 Owner发布、技术真实性确认、合法认证、作业授权或能力认证。尚未连接正式审核与
          Owner 发布。各端独立演示，正式跨端流转等待BIZ04。
        </p>
      </div>
    </aside>
  );
}
